'use strict';

/**
 * Answers adapter — hands a widget its own answers as plain data.
 *
 * The single seam between the AnswerStore and the domain layer. A domain rule
 * takes `(question, answers)` and may not touch `Gryphon`, so something has to
 * work out which variables a question owns and read them out of the store. That
 * is this module, and only this module, so widgets never reach into
 * `Gryphon.answers` themselves and the domain layer stays pure.
 *
 * Read-only: nothing here writes to the store or to the question object. The
 * write direction is `utils/lib/answer-sync.js`.
 *
 * Three files, split by what each is allowed to touch:
 *
 *   - `variables.js`  — which variables a question declares. Pure; reads the
 *                       question object and nothing else.
 *   - `undeclared.js` — telemetry for derived names the store can never hold.
 *                       The only part that reads ambient state.
 *   - `index.js`      — this file. Reads the values and returns the snapshot,
 *                       and is the whole public surface: import from
 *                       `../answers-adapter`, never from a file inside it.
 *
 * The store arrives as the module's own export rather than through
 * `Gryphon.answers`. Both name the same object — `answer-store` self-registers
 * the global on import, and `global.js` already imports it at bootstrap — but
 * the import cannot be absent, so there is no "store missing" path to read
 * every variable as `undefined` through. Code that deliberately swaps
 * `Gryphon.answers` will not be followed here.
 */
import store from '../../../utils/lib/answer-store';
import { variablesOf } from './variables';
import { reportUndeclared } from './undeclared';

export { answerVariablesOf } from './variables';

/**
 * Unwrap a widget instance to the question object it was constructed with.
 *
 * Callers pass the widget (`answersForWidget(this)`), matching how the existing
 * shared helpers take `self` — `isRequiredSatisfied(self)`,
 * `getSelectedRowsCount(self)`, `getDefaultPhaseRange(self)`.
 *
 * @param {Object} widgetObject A widget instance, or a question object.
 * @returns {?Object} The question object, or `null` if there isn't one.
 */
function questionOf(widgetObject) {
  if (!widgetObject || typeof widgetObject !== 'object') {
    return null;
  }
  return widgetObject._object || widgetObject;
}

/**
 * One widget's answers, as a plain snapshot keyed by variable name.
 *
 * The key set is the question's full declared variable list, so a variable the
 * respondent has not answered is present with the value `undefined` rather than
 * missing. A rule can therefore enumerate a question's declared variables from
 * the snapshot alone, without walking the question object again.
 *
 * What a value means, and it is three states rather than two:
 *
 *   - `undefined` — the store holds no entry. `get_form_value` never returns
 *     `undefined` (`globals/form.js:9-63` returns `''` on every no-selection
 *     path), and `answer-sync.js:148` stores exactly what it returns, so
 *     `undefined` only ever means "nothing has written this variable yet."
 *   - `''` — something wrote it while it held no selection. An unchecked
 *     checkbox, an unselected radio, a cleared text field and a variable absent
 *     from `document.forms[0]` all produce `''` (`form.js:12,25,37,45,57,62`).
 *   - any other string — answered. A checked checkbox carries no `value`
 *     attribute in `_response-item.hbs`, so the browser reports `'on'` and that
 *     is what is stored. (`form.js:52` returns `'1'` for a nullish `value`, but
 *     a DOM checkbox's `value` defaults to the string `'on'`, so that branch is
 *     unreachable from real markup. `shadow-compare.js` names the same fact in
 *     its `on-vs-code` class.)
 *
 * So **unanswered is `undefined` or `''`**, never just one of them. A rule that
 * tests only `value !== undefined` will read an unchecked box as answered.
 *
 * Two things the key set deliberately does NOT tell a rule:
 *
 *   - Whether a variable was asked. `undefined` covers both "never rendered"
 *     and "rendered but untouched", because neither is ever written. The
 *     inclusiveness note on `variablesOf` depends on exactly this.
 *   - Whether the RESPONDENT has done anything. `utils/lib/answer-seed.js`
 *     builds the page's initial answer state from the API response, so at load
 *     the store already holds recorded answers (`form_data`), `passthru`
 *     values, and any `question.answer` the scripter set as a default — for
 *     `single`, `dropdown` and `open` under `question.input_id`, and for
 *     `multiple` as `'on'` against each selected option's `input_name`.
 *     `answerVariablesOf` removes `passthru` from the count, but NOT a
 *     default: a default is rendered as selected and would submit, so a rule
 *     counting it as answered matches what the DOM-based implementations saw.
 *     Do not read a value as evidence the respondent acted.
 *
 * Widgets whose answer state is not a plain `question.answer` — grid, rank,
 * cardsort — are not seeded at all, so `undefined` stays the normal reading for
 * their variables until an edit. `state.js` publishes the gap as
 * `page_state.unseeded_variables`.
 *
 * The snapshot is a copy, built fresh per call: adding, removing, or replacing
 * its keys cannot reach the store. Values are strings or `undefined`, so a copy
 * of the keys is the whole copy. Treat values as read-only.
 *
 * Returns `{}` rather than throwing when handed nothing usable, so a caller on
 * a half-built widget degrades to "no answers" instead of breaking the page.
 *
 * @param {Object} widgetObject A widget instance, or a question object.
 * @returns {Object} Map of variable name to stored value.
 */
export function answersForWidget(widgetObject) {
  var snapshot = {};
  var question = questionOf(widgetObject);
  if (!question) {
    return snapshot;
  }

  var names = variablesOf(question);
  reportUndeclared(names);
  for (var i = 0; i < names.length; i++) {
    // `defineProperty` rather than assignment so that every derived name
    // becomes an own enumerable key. Plain assignment of `__proto__` is
    // swallowed by the inherited setter, which would drop it from
    // `Object.keys` and quietly break the promise above that the key set is
    // the full declared list.
    Object.defineProperty(snapshot, names[i], {
      value: store.get(names[i]),
      enumerable: true,
      writable: true,
      configurable: true,
    });
  }
  return snapshot;
}
